import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import CompanyFeatureFlagsEnum, { CompanyFeatureFlagsTips } from 'ab-enums/companyFeatureFlags.enum';

import { Button } from '@acceligentllc/storybook';

import Checkbox from 'af-components/Controls/Checkbox';
import TimeInput from 'af-components/Controls/TimeInput';
import CustomModal from 'af-components/CustomModal';
import SubmitButton from 'af-components/SubmitButton';

import * as CompanyActions from 'af-actions/companies';

import styles from './styles.module.scss';
import PreviewItem from '../../Shared/PreviewItem';

type OwnProps = {
	showModal: boolean;
	closeModal: () => void;
	isJobTrackingEnabled: boolean;
	jobTrackingSyncTime: number;
};

type Props = OwnProps & ConnectedProps<typeof connector>;

const EnableJobTrackingCompanyEdit: React.FC<Props> = (props: Props) => {
	const { closeModal, showModal, isJobTrackingEnabled, editJobTracking, jobTrackingSyncTime  } = props;

	const [enableJobTracking, setEnableJobTracking] = React.useState(isJobTrackingEnabled);
	const [syncTime, setSyncTime] = React.useState<Nullable<number>>(jobTrackingSyncTime);

	const renderLabel = React.useCallback(() => {
		return (
			<PreviewItem
				checked={!!isJobTrackingEnabled}
				hint={CompanyFeatureFlagsTips.jobTracking}
				indent={false}
				label={CompanyFeatureFlagsEnum.isJobTrackingEnabled}
				showCheckbox={false}
			/>
		);
	}, [isJobTrackingEnabled]);

	const handleChange = React.useCallback((event) => {
		const isChecked = event.target.checked;
		setEnableJobTracking(isChecked);

		if (!isChecked) {
			setSyncTime(null);
		}
	}, []);

	const handleSubmit = React.useCallback(async () => {
		await editJobTracking({
			isJobTrackingEnabled: enableJobTracking,
			syncTime: syncTime,
		});
		closeModal();
	}, [editJobTracking, enableJobTracking, syncTime, closeModal]);

	return (
		<CustomModal
			closeModal={closeModal}
			modalStyle="info"
			showModal={showModal}
			size="lg"
		>
			<CustomModal.Header
				closeModal={closeModal}
				title="Edit Job Tracking"
			/>
			<CustomModal.Body className={styles['job-tracking-modal__body']}>
				<div className={styles['job-tracking-modal__fields']}>
					<div className="job-tracking-modal__field">
						<Checkbox
							handleChange={handleChange}
							isChecked={enableJobTracking}
							label={renderLabel()}
						/>
					</div>
					<div className={styles['job-tracking-modal__field']}>
						<span>Sync JobTracking at: </span>
						<TimeInput
							className={styles['job-tracking-modal__time-input']}
							disabled={!enableJobTracking}
							drop={'right'}
							id="job-tracking-sync-time"
							minInterval={15}
							onChange={setSyncTime}
							value={syncTime}
						/>
					</div>
				</div>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={closeModal}
					style="secondary"
				/>
				<SubmitButton
					label="Save"
					onClick={handleSubmit}
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapDispatchToProps() {
	return {
		editJobTracking: CompanyActions.editJobTracking,
	};
}

const connector = connect(null, mapDispatchToProps());

export default connector(EnableJobTrackingCompanyEdit);
